<template>
  <div class="expandable">
    <div
      ref="listWrapper"
      class="expandable__content"
      :class="{
        expanded: isExpanded,
      }"
      :style="{
        maxHeight: isExpanded ? 'none' : +maxVisibleRows * (+rowHeight + +gap) + 'px',
      }"
      @focusin="onItemFocus"
    >
      <slot />
    </div>
  </div>
  <button
    v-if="isExpandable"
    class="expandable__button grid-x align-middle align-center margin-top-xs"
    @click="toggleExpand"
  >
    {{ isExpanded ? collapsedText : expandedText }}
    <i :class="isExpanded ? 'chevron-medium-up-black' : 'chevron-medium-down-black'" />
  </button>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import { useMounted, useWindowSize } from '@vueuse/core';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  expandedText: {
    type: String,
    default: 'Show more',
  },
  collapsedText: {
    type: String,
    default: 'Show less',
  },
  activeItemIndex: {
    type: Number,
    default: -1,
  },
  isCurrentPaneSelected: {
    type: Boolean,
    default: true,
  },
  maxVisibleRows: {
    type: [Number, String],
    default: 2,
  },
  rowHeight: {
    type: [Number, String],
    default: 0,
  },
  gap: {
    type: [Number, String],
    default: 0,
  },
  options: {
    type: Array,
    default: () => [],
  },
  listSelector: {
    type: String,
    default: 'li',
  },
});

const emits = defineEmits(['update:modelValue', 'expandable']);
/**
 * @type {import('vue').Ref<HTMLElement | null>}
 */
const listWrapper = ref(null);
const { width } = useWindowSize();
const isMounted = useMounted();

/**
 * @type {import('vue').Ref<number | null>}
 */
const breakpoint = ref(null);

const isExpanded = computed({
  set(value) {
    emits('update:modelValue', value);
  },
  get() {
    return props.modelValue;
  },
});
const isInSize = computed(() => {
  if (!isMounted.value) {
    return false;
  }
  if (breakpoint.value !== null && width.value < breakpoint.value) {
    return true;
  }
  if (listWrapper.value) {
    const height = listWrapper.value.clientHeight + +props.gap;
    width.value; // eslint-disable-line no-unused-expressions
    return listWrapper.value.scrollHeight > height;
  }
  return false;
});

const isExpandable = computed(() => {
  if (isExpanded.value) {
    return true;
  }
  return isInSize.value;
});

watch(isInSize, (value) => {
  if (value && breakpoint.value === null) {
    breakpoint.value = width.value;
  }
  if (isExpanded.value && !value) {
    isExpanded.value = false;
  }
});

watch(isExpandable, (value) => {
  emits('expandable', value);
}, { immediate: true });

const onItemFocus = () => {
  if (listWrapper.value === null) {
    return;
  }
  const listWrapperScroll = listWrapper.value.scrollTop;
  if (listWrapperScroll) {
    listWrapper.value.scrollTop = 0;
    isExpanded.value = true;
  }
};

watch(() => props.activeItemIndex, (value) => {
  if (value !== -1) {
    onItemFocus();
  }
});

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

defineExpose({ // eslint-disable-line no-undef
  isExpandable,
  toggleExpand,
});
</script>

<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-medium-down-black.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/_chevron-medium-up-black.scss';

.expandable {
  position: relative;
  overflow-y: hidden;
  transition: max-height 0.3s ease-in-out;
  container-type: inline-size;

  &__content {
    overflow: hidden;
    transition: max-height 0.3s ease-in-out;

    &.expanded {
      transform: translateY(0);
      max-height: none;
    }
  }
}

.expandable__button {
  line-height: 20px;
  cursor: pointer;

  @include breakpoint(small down) {
    display: none;
  }
}

.expandable__invisible {
  visibility: hidden;
  position: absolute;
  z-index: -1;
  opacity: 0;
  pointer-events: none;
}
</style>
